import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, Search, Download, Upload, Percent, Check, X, History } from "lucide-react";
import api from "../../lib/api";
import { fmtTRY, fmtQty4  } from "../../lib/format";
import { errText } from "../../lib/errors";
import { toast } from "sonner";
import { downloadApi } from "../../lib/download";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";
const EMPTY_FILTERS = { category_id: "", marka: "", stok: "", min_fiyat: "", max_fiyat: "" };

export default function AdminProducts() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [cats, setCats] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [quickPrice, setQuickPrice] = useState({});
  const fileRef = useRef(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulk, setBulk] = useState({ scope: "all", mode: "percent", direction: "increase", value: "", apply_discount_too: false });
  const [selected, setSelected] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyFor, setHistoryFor] = useState("");

  const items = data?.items || null;
  const brands = data?.brands || [];

  const reqSeq = useRef(0);
  const load = (f = filters, query = q) => {
    const qs = new URLSearchParams();
    if (query) qs.set("q", query);
    Object.entries(f).forEach(([k, v]) => { if (v) qs.set(k, v); });
    const seq = ++reqSeq.current;
    api.get(`/admin/products?${qs.toString()}`).then(({ data }) => { if (seq === reqSeq.current) setData(data); }).catch(() => {});
  };
  const setFilterNow = (k, v) => { const f = { ...filters, [k]: v }; setFilters(f); load(f); };
  useEffect(() => {
    load();
    api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  const dlExcel = async () => {
    try {
      await downloadApi("/admin/products-export", "urunler.xlsx");
      toast.success("urunler.xlsx indirildi");
    } catch { toast.error("Dışa aktarma başarısız"); }
  };

  const upExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/products-import", fd);
      toast.success(`İçe aktarma tamamlandı — ${data.created} yeni, ${data.updated} güncellendi`);
      if (data.errors?.length) toast.warning(`${data.errors.length} satır atlandı: ${data.errors[0].error} (satır ${data.errors[0].row})${data.errors.length > 1 ? " vb." : ""}`);
      load();
    } catch (err) { toast.error(errText(err) || "İçe aktarma başarısız"); }
    finally { e.target.value = ""; }
  };

  const applyBulk = async () => {
    if (!bulk.value || Number(bulk.value) <= 0) return toast.error("Geçerli bir değer girin");
    if (bulk.scope === "selected" && !selected.length) return toast.error("Ürün seçilmedi");
    try {
      const { data } = await api.post("/admin/products-bulk-price", { ...bulk, value: Number(bulk.value), product_ids: bulk.scope === "selected" ? selected : [] });
      toast.success(`${data.updated} ürünün fiyatı güncellendi`);
      setBulkOpen(false);
      setSelected([]);
      load();
    } catch (err) { toast.error(errText(err) || "İşlem başarısız"); }
  };

  const toggleSel = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const toggleAll = () => setSelected((s) => (items && s.length === items.length ? [] : (items || []).map((p) => p.product_id)));

  const openHistory = async (p) => {
    try {
      const { data } = await api.get(p ? `/admin/price-history?product_id=${p.product_id}` : "/admin/price-history");
      setHistory(data);
      setHistoryFor(p ? p.name : "");
      setHistoryOpen(true);
    } catch { toast.error("Geçmiş yüklenemedi"); }
  };

  const saveQuickPrice = async (p) => {
    const newPrice = Number(quickPrice[p.product_id]);
    if (!newPrice || newPrice <= 0) return toast.error("Geçerli bir fiyat girin");
    if (newPrice === p.price) return toast.info("Fiyat zaten aynı");
    try {
      await api.put(`/admin/products/${p.product_id}/quick-price`, { price: newPrice });
      toast.success(`${p.name}: ${fmtTRY(p.price)} → ${fmtTRY(newPrice)}`);
      load();
    } catch (err) { toast.error(errText(err) || "Güncellenemedi"); }
  };

  const del = async (p) => {
    if (!window.confirm(`"${p.name}" silinsin mi?`)) return;
    await api.delete(`/admin/products/${p.product_id}`);
    toast.success("Ürün silindi");
    load();
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-serif text-2xl sm:text-3xl text-forest">Ürünler</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button data-testid="excel-export-button" onClick={dlExcel} className="flex items-center gap-2 bg-white border border-stone-300 hover:border-forest text-stone-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><Download className="w-4 h-4" /> Excel İndir</button>
          <button data-testid="excel-import-button" onClick={() => fileRef.current?.click()} className="flex items-center gap-2 bg-white border border-stone-300 hover:border-forest text-stone-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><Upload className="w-4 h-4" /> Excel Yükle</button>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={upExcel} data-testid="excel-file-input" />
          <button data-testid="bulk-price-button" onClick={() => setBulkOpen(true)} className="flex items-center gap-2 bg-gold hover:bg-gold-dark text-stone-900 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><Percent className="w-4 h-4" /> Toplu Fiyat</button>
          <button data-testid="price-history-button" onClick={() => openHistory(null)} className="flex items-center gap-2 bg-white border border-stone-300 hover:border-forest text-stone-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"><History className="w-4 h-4" /> Fiyat Geçmişi</button>
          <Link to="/admin/urunler/yeni" data-testid="add-product-button" className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Ürün</Link>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-stone-200/80 p-4 mt-5 shadow-sm">
        <div className="flex gap-3 flex-wrap items-end">
          <div className="relative flex-1 min-w-52">
            <input data-testid="admin-product-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="İsim, SKU veya barkod ara..." className={`${inputCls} pl-10`} />
            <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-3.5" />
          </div>
          <select data-testid="filter-category" className={`${inputCls} w-40`} value={filters.category_id} onChange={(e) => setFilterNow("category_id", e.target.value)}>
            <option value="">Tüm Kategoriler</option>
            {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
          </select>
          <select data-testid="filter-brand" className={`${inputCls} w-36`} value={filters.marka} onChange={(e) => setFilterNow("marka", e.target.value)}>
            <option value="">Tüm Markalar</option>
            {brands.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <select data-testid="filter-stock" className={`${inputCls} w-36`} value={filters.stok} onChange={(e) => setFilterNow("stok", e.target.value)}>
            <option value="">Stok: Tümü</option><option value="var">Stokta Var</option><option value="yok">Stokta Yok</option><option value="kritik">Kritik Stok</option>
          </select>
          <input data-testid="filter-min-price" type="number" placeholder="Min ₺" className={`${inputCls} w-24`} value={filters.min_fiyat} onChange={(e) => setFilters({ ...filters, min_fiyat: e.target.value })} />
          <input data-testid="filter-max-price" type="number" placeholder="Max ₺" className={`${inputCls} w-24`} value={filters.max_fiyat} onChange={(e) => setFilters({ ...filters, max_fiyat: e.target.value })} />
          <button data-testid="filter-apply" onClick={() => load()} className="bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors">Filtrele</button>
          <button data-testid="filter-clear" onClick={() => { setQ(""); setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS, ""); }} className="flex items-center gap-1 text-stone-500 text-sm font-semibold px-3 py-2.5 rounded-xl hover:bg-cream-dark transition-colors"><X className="w-4 h-4" /> Temizle</button>
        </div>
      </div>

      {selected.length > 0 && (
        <div data-testid="selection-bar" className="mt-4 bg-gold/15 border border-gold/40 rounded-xl px-4 py-2.5 flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-semibold text-stone-800">{selected.length} ürün seçili</p>
          <div className="flex gap-2">
            <button data-testid="bulk-selected-button" onClick={() => { setBulk({ ...bulk, scope: "selected" }); setBulkOpen(true); }} className="flex items-center gap-1.5 bg-forest hover:bg-forest-dark text-white text-xs font-semibold px-4 py-2 rounded-xl transition-colors"><Percent className="w-3.5 h-3.5" /> Seçilenlere Toplu Fiyat</button>
            <button data-testid="selection-clear" onClick={() => setSelected([])} className="text-xs font-semibold text-stone-500 hover:text-stone-700 px-3 py-2 rounded-xl hover:bg-stone-100 transition-colors">Seçimi Temizle</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-stone-200/80 mt-5 overflow-x-auto shadow-sm">
        <table className="w-full text-sm min-w-[860px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-100"><th className="p-4 w-10"><input type="checkbox" data-testid="select-all-products" checked={!!items?.length && selected.length === items.length} onChange={toggleAll} className="w-4 h-4 accent-forest" /></th><th className="p-4">Ürün</th><th className="p-4">SKU / Barkod</th><th className="p-4">Kategori</th><th className="p-4">Hızlı Fiyat</th><th className="p-4 text-right">Stok</th><th className="p-4 text-right">İşlem</th></tr></thead>
          <tbody>
            {(items || []).map((p) => (
              <tr key={p.product_id} data-testid={`product-row-${p.sku}`} className={`border-b border-stone-50 hover:bg-cream/60 ${selected.includes(p.product_id) ? "bg-gold/10" : ""}`}>
                <td className="p-4 w-10"><input type="checkbox" data-testid={`select-product-${p.sku}`} checked={selected.includes(p.product_id)} onChange={() => toggleSel(p.product_id)} className="w-4 h-4 accent-forest" /></td>
                <td className="p-4"><div className="flex items-center gap-3"><img src={p.images?.[0]} alt="" className="w-10 h-10 rounded-lg object-cover bg-cream-dark" /><div><span className="font-medium">{p.name}</span>{p.discount_price && <span className="block text-xs text-terra">İndirimli: {fmtTRY(p.discount_price)}</span>}</div></div></td>
                <td className="p-4 font-mono text-xs">{p.sku}<br /><span className="text-stone-400">{p.barcode}</span></td>
                <td className="p-4 text-stone-600">{p.category_name}</td>
                <td className="p-4">
                  <div className="flex items-center gap-1.5">
                    <input
                      data-testid={`quick-price-${p.sku}`}
                      type="number" step="0.01" min="0"
                      defaultValue={p.price}
                      key={`${p.product_id}-${p.price}`}
                      onChange={(e) => setQuickPrice({ ...quickPrice, [p.product_id]: e.target.value })}
                      className="w-24 h-9 px-2.5 rounded-lg border border-stone-300 text-sm text-right focus:outline-none focus:ring-2 focus:ring-forest/30"
                    />
                    <button data-testid={`quick-price-save-${p.sku}`} onClick={() => saveQuickPrice(p)} title="Fiyatı güncelle" className="p-2 rounded-lg bg-forest hover:bg-forest-dark text-white transition-colors"><Check className="w-4 h-4" /></button>
                  </div>
                </td>
                <td className={`p-4 text-right font-bold ${p.stock <= p.min_stock ? "text-terra" : "text-stone-800"}`}>{fmtQty4(p.stock, p.base_unit || p.unit)}</td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-1">
                    <button data-testid={`history-product-${p.sku}`} onClick={() => openHistory(p)} title="Fiyat geçmişi" className="p-2 rounded-lg hover:bg-cream-dark text-stone-600"><History className="w-4 h-4" /></button>
                    <Link to={`/admin/urunler/${p.product_id}`} data-testid={`edit-product-${p.sku}`} className="p-2 rounded-lg hover:bg-cream-dark text-stone-600"><Pencil className="w-4 h-4" /></Link>
                    <button data-testid={`delete-product-${p.sku}`} onClick={() => del(p)} className="p-2 rounded-lg hover:bg-red-50 text-terra"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items === null && <p className="p-8 text-center text-stone-400">Yükleniyor...</p>}
        {items?.length === 0 && <p className="p-8 text-center text-stone-400">Ürün bulunamadı</p>}
      </div>

      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Toplu Fiyat Güncelleme</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs font-semibold text-stone-600 uppercase tracking-wider block mb-1.5">Kapsam</label>
              <select data-testid="bulk-scope" className={inputCls} value={bulk.scope} onChange={(e) => setBulk({ ...bulk, scope: e.target.value })}>
                <option value="all">Tüm Ürünler</option>
                <option value="selected" disabled={selected.length === 0}>Seçili Ürünler ({selected.length})</option>
                {cats.map((c) => <option key={c.category_id} value={c.category_id}>{c.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select data-testid="bulk-mode" className={inputCls} value={bulk.mode} onChange={(e) => setBulk({ ...bulk, mode: e.target.value })}>
                <option value="percent">Yüzde (%)</option>
                <option value="fixed">Sabit Tutar (TL)</option>
              </select>
              <select data-testid="bulk-direction" className={inputCls} value={bulk.direction} onChange={(e) => setBulk({ ...bulk, direction: e.target.value })}>
                <option value="increase">Artır</option>
                <option value="decrease">Azalt</option>
              </select>
            </div>
            <input data-testid="bulk-value" type="number" step="0.01" min="0" className={inputCls} placeholder="Değer (örn. 10)" value={bulk.value} onChange={(e) => setBulk({ ...bulk, value: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
              <input data-testid="bulk-apply-discount" type="checkbox" className="w-4 h-4 accent-forest" checked={bulk.apply_discount_too} onChange={(e) => setBulk({ ...bulk, apply_discount_too: e.target.checked })} />
              İndirimli fiyatlara da uygula
            </label>
            <p className="text-xs text-stone-400">İşlem geri alınamaz; tüm değişiklikler denetim kaydına yazılır.</p>
            <button data-testid="bulk-apply" onClick={applyBulk} className="w-full bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors">Uygula</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>Fiyat Geçmişi {historyFor && `— ${historyFor}`}</DialogTitle><DialogDescription className="sr-only">Ürün fiyat değişikliği geçmişi</DialogDescription></DialogHeader>
          <div data-testid="price-history-list" className="max-h-[60vh] overflow-y-auto divide-y divide-stone-100 mt-2">
            {history.map((h) => (
              <div key={h.history_id} className="py-2.5 flex items-center gap-3 text-sm">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-900 truncate">{h.name} <span className="text-xs text-stone-400 font-mono">{h.sku}</span></p>
                  <p className="text-xs text-stone-400">{h.created_at.slice(0, 16).replace("T", " ")} • {h.by}</p>
                </div>
                <p className="text-stone-400 line-through text-xs">{fmtTRY(h.old_price)}</p>
                <p className="font-bold text-forest">{fmtTRY(h.new_price)}</p>
              </div>
            ))}
            {history.length === 0 && <p className="py-8 text-center text-stone-400 text-sm">Henüz fiyat değişikliği kaydı yok</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
